/**
 * 쿠키 관리 유틸리티
 */
import { isValidJWT } from './jwt';

export interface CookieOptions {
  expires?: Date | number; // Date 객체 또는 만료일까지의 일수
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean; // JavaScript에서는 실제로 설정할 수 없지만 타입 완성도를 위해 포함
}

/**
 * 쿠키 설정
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): void => {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // 만료일 설정
  if (options.expires) {
    let expires: Date;
    if (typeof options.expires === 'number') {
      expires = new Date();
      expires.setTime(
        expires.getTime() + options.expires * 24 * 60 * 60 * 1000
      );
    } else {
      expires = options.expires;
    }
    cookieString += `; expires=${expires.toUTCString()}`;
  }

  // 경로 설정
  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += `; path=/`; // 기본값으로 루트 경로 설정
  }

  // 도메인 설정
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  // 보안 설정
  if (options.secure) {
    cookieString += `; secure`;
  }

  // SameSite 설정
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  // 쿠키 크기 제한 확인 (대부분 브라우저는 4KB 제한)
  if (cookieString.length > 4000) {
    return;
  }

  document.cookie = cookieString;
};

/**
 * 쿠키 조회
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      const value = decodeURIComponent(cookie.substring(nameEQ.length));
      return value;
    }
  }
  return null;
};

/**
 * 쿠키 삭제
 */
export const removeCookie = (
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {}
): void => {
  setCookie(name, '', {
    ...options,
    expires: new Date(0), // 과거 날짜로 설정하여 삭제
  });
};

/**
 * 모든 쿠키 조회
 */
export const getAllCookies = (): Record<string, string> => {
  const cookies: Record<string, string> = {};
  const cookieList = document.cookie.split(';');

  for (let i = 0; i < cookieList.length; i++) {
    const cookie = cookieList[i].trim();
    const [name, value] = cookie.split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  }

  return cookies;
};

/**
 * 인증 관련 쿠키 상수
 */
export const AUTH_COOKIES = {
  ACCESS_TOKEN: 'token',
} as const;

/**
 * 큰 토큰을 여러 개의 작은 쿠키로 분할하여 저장
 */
export const setAuthToken = (token: string): void => {
  if (!token) {
    clearTokenCookies();
    return;
  }

  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + 7 * 24 * 60 * 60 * 1000); // 7일
    const expiresString = expires.toUTCString();

    // 토큰 크기 확인
    const encodedTokenSize = encodeURIComponent(token).length;

    // 쿠키 하나당 최대 3000바이트로 제한 (더 안전한 마진)
    const maxChunkSize = 3000;

    if (encodedTokenSize <= maxChunkSize) {
      // 작은 토큰은 그대로 저장
      const cookieString = `${AUTH_COOKIES.ACCESS_TOKEN}=${encodeURIComponent(token)}; expires=${expiresString}; path=/; SameSite=Lax`;
      document.cookie = cookieString;
    } else {
      // 기존 분할 쿠키들 먼저 삭제
      clearTokenCookies();

      const chunks = [];
      for (let i = 0; i < token.length; i += maxChunkSize) {
        chunks.push(token.slice(i, i + maxChunkSize));
      }

      // 분할된 토큰들을 각각 저장
      chunks.forEach((chunk, index) => {
        const chunkName = `${AUTH_COOKIES.ACCESS_TOKEN}_${index}`;
        const encodedChunk = encodeURIComponent(chunk);
        const cookieString = `${chunkName}=${encodedChunk}; expires=${expiresString}; path=/; SameSite=Lax`;

        document.cookie = cookieString;
      });

      // 총 분할 개수를 별도 쿠키에 저장
      const countCookie = `${AUTH_COOKIES.ACCESS_TOKEN}_count=${chunks.length}; expires=${expiresString}; path=/; SameSite=Lax`;
      document.cookie = countCookie;
    }

    // 저장 확인
    setTimeout(() => {
      const retrieved = getAuthToken();
      const isSuccess = !!retrieved;
      const isMatching = retrieved === token;

      if (!isSuccess) {
        console.error('[setAuthToken] 토큰 저장 실패 - 조회되지 않음');
      } else if (!isMatching) {
        console.error('[setAuthToken] 토큰 내용 불일치 - 데이터 손상 가능성');
        console.error('원본 앞부분:', token.substring(0, 100));
        console.error('복원 앞부분:', retrieved?.substring(0, 100));
      } else {
        console.log('[setAuthToken] 토큰 저장 및 복원 완벽 성공');
      }
    }, 200);
  } catch (error) {
    console.error('[setAuthToken] 토큰 쿠키 설정 실패:', error);
  }
};

/**
 * 분할된 토큰 쿠키들 모두 삭제
 */
const clearTokenCookies = (): void => {
  // 단일 토큰 쿠키 삭제
  removeCookie(AUTH_COOKIES.ACCESS_TOKEN);

  // 분할된 토큰 쿠키들 삭제 (최대 20개까지 확인)
  for (let i = 0; i < 20; i++) {
    removeCookie(`${AUTH_COOKIES.ACCESS_TOKEN}_${i}`);
  }

  // count 쿠키 삭제
  removeCookie(`${AUTH_COOKIES.ACCESS_TOKEN}_count`);
};

/**
 * 분할된 쿠키들을 조합하여 토큰 복원
 */
export const getAuthToken = (): string | null => {
  // 먼저 단일 쿠키에서 토큰 확인
  const token = getCookie(AUTH_COOKIES.ACCESS_TOKEN);

  if (token) {
    return decodeURIComponent(token);
  }

  // 분할된 쿠키들에서 토큰 복원 시도
  const countStr = getCookie(`${AUTH_COOKIES.ACCESS_TOKEN}_count`);

  if (!countStr) {
    return null;
  }

  const chunkCount = Number(countStr);
  if (Number.isNaN(chunkCount) || chunkCount <= 0) {
    return null;
  }

  // 분할된 토큰들을 순서대로 조합
  const chunks: string[] = [];
  for (let i = 0; i < chunkCount; i++) {
    const chunkName = `${AUTH_COOKIES.ACCESS_TOKEN}_${i}`;
    const chunk = getCookie(chunkName);

    if (!chunk) {
      return null;
    }
    chunks.push(decodeURIComponent(chunk));
  }

  const restoredToken = chunks.join('');
  return restoredToken;
};

/**
 * 인증 관련 쿠키 모두 삭제
 */
export const clearAuthCookies = (): void => {
  clearTokenCookies();
};

/**
 * 디버깅용: 현재 인증 상태 확인
 */
export const debugAuthCookies = (): void => {
  const token = getAuthToken();
  console.log('[debugAuthCookies] 현재 인증 쿠키 상태:', {
    전체쿠키: document.cookie,
    토큰: token ? '***' + token.slice(-10) : null,
    토큰길이: token?.length || 0,
    JWT유효성: token ? isValidJWT(token) : false,
  });
};
